import React from "react";
import { LINKS, LOGOS, BACKGROUNDS } from "@/constants/links";
import StatusBadge from "@/components/StatusBadge";
import { ArrowUpRight } from "lucide-react";

export const GamesShowcase = () => {
  return (
    <section
      id="games"
      data-testid="games-section"
      className="relative py-24 md:py-40"
    >
      <div className="mx-auto max-w-7xl px-6 md:px-10">
        <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
          <div>
            <p className="eyebrow">Our Games</p>
            <h2
              data-testid="games-heading"
              className="font-serif-display mt-4 text-4xl leading-tight text-[#F2EFEB] sm:text-5xl lg:text-6xl"
            >
              Two worlds. <em className="italic text-[#C9A87C]">One loom.</em>
            </h2>
          </div>
          <p className="max-w-md text-sm leading-relaxed text-[#A39D95]">
            Both games share Tapestry Games&apos; craft — sharp rules, evocative
            fiction, and rituals of play designed to reward the table.
          </p>
        </div>

        <div className="mt-16 grid grid-cols-1 gap-8 lg:grid-cols-2">
          {/* God Complex Card */}
          <a
            href={LINKS.godComplexSite}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="game-card-godcomplex"
            className="game-card group relative flex min-h-[620px] flex-col justify-between overflow-hidden"
          >
            <img
              src={BACKGROUNDS.godComplex}
              alt=""
              aria-hidden="true"
              className="card-bg absolute inset-0 h-full w-full object-cover opacity-40"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0A0908] via-[#0A0908]/70 to-[#0A0908]/30" />
            <span className="corner-orn tl" />
            <span className="corner-orn tr" />
            <span className="corner-orn bl" />
            <span className="corner-orn br" />

            <div className="relative z-10 flex items-start justify-between p-8 md:p-10">
              <StatusBadge label="Beta" tone="gold" testid="badge-godcomplex" />
              <span className="text-[10px] uppercase tracking-[0.32em] text-[#6E6A64]">
                Urban Fantasy
              </span>
            </div>

            <div className="relative z-10 flex flex-col items-start gap-6 p-8 md:p-10">
              <img
                src={LOGOS.godComplex}
                alt="God Complex"
                className="invert-logo h-24 w-auto object-contain md:h-32"
                data-testid="game-card-godcomplex-logo"
              />
              <h3 className="font-serif-display text-3xl text-[#F2EFEB] md:text-4xl">
                God Complex
              </h3>
              <p className="max-w-md text-sm leading-relaxed text-[#A39D95] md:text-base">
                Newly awakened Immortals — Elysians — walk a modern city where
                vampires, demons, and ambitious rivals still remember the old
                names. Balance humanity against a growing God Complex.
                Currently in open Beta.
              </p>
              <span className="mt-2 inline-flex items-center gap-2 text-xs uppercase tracking-[0.24em] text-[#C9A87C] transition-transform duration-300 group-hover:translate-x-1">
                Visit GodComplexTTRPG.com
                <ArrowUpRight className="h-4 w-4" />
              </span>
            </div>
          </a>

          {/* Fatewoven Card */}
          <div
            data-testid="game-card-fatewoven"
            className="game-card fatewoven-card group relative flex min-h-[620px] flex-col justify-between overflow-hidden"
          >
            <img
              src={BACKGROUNDS.fatewoven}
              alt=""
              aria-hidden="true"
              className="card-bg absolute inset-0 h-full w-full object-cover opacity-35"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0A0908] via-[#0A0908]/75 to-[#0A0908]/40" />
            <span className="corner-orn tl" />
            <span className="corner-orn tr" />
            <span className="corner-orn bl" />
            <span className="corner-orn br" />

            <div className="relative z-10 flex items-start justify-between p-8 md:p-10">
              <StatusBadge
                label="Pre-Alpha"
                tone="green"
                testid="badge-fatewoven"
              />
              <span className="text-[10px] uppercase tracking-[0.32em] text-[#6E6A64]">
                High Fantasy
              </span>
            </div>

            <div className="relative z-10 flex flex-col items-start gap-6 p-8 md:p-10">
              <img
                src={LOGOS.fatewoven}
                alt="Fatewoven"
                className="h-24 w-auto object-contain md:h-32"
                data-testid="game-card-fatewoven-logo"
              />
              <h3 className="font-serif-display text-3xl text-[#F2EFEB] md:text-4xl">
                Fatewoven
              </h3>
              <p className="max-w-md text-sm leading-relaxed text-[#A39D95] md:text-base">
                Heroes woven into the fabric of fate, in a world where arcane
                magic has returned to clash with the old divine orders. Chart
                your Mythos through court, guild, and destiny. Currently in
                Pre-Alpha development.
              </p>
              <span
                data-testid="fatewoven-site-note"
                className="mt-2 inline-flex items-center gap-2 text-xs uppercase tracking-[0.24em] text-[#6E6A64]"
              >
                <span className="pulse-dot inline-block h-1.5 w-1.5 rounded-full bg-emerald-400" />
                FateWovenTTRPG.com — site coming soon
              </span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default GamesShowcase;
